import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, notification, Form, Input } from 'antd';
import { ThemeModal } from '../../pages/Styles/Theme';
import {
  getUserByIdApi,
  getUserByIdReset,
  updateUserApi,
  updateUserReset,
} from '../../store/actions/userAction';
import { getUserId, getUserData, STORAGE_KEYS } from '../../utils/permissions';
import styled from 'styled-components';

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.settings.primary};
  }
  .ant-form-item {
    margin-bottom: 16px;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  height: 44px;
  font-size: 14px;
  &:focus, &:hover {
    border-color: ${({ theme }) => theme.settings.primary};
  }
`;

const ProfileEditModal = (props) => {
  const {
    open,
    onClose,
    getUserById,
    getUserByIdReset,
    getUserByIdState,
    updateUser,
    updateUserReset,
    updateUserState,
  } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userId = getUserId();

  // Fetch user data when modal opens
  useEffect(() => {
    if (open && userId) {
      getUserById(userId);
    }
  }, [open, userId, getUserById]);

  // Handle user data response
  useEffect(() => {
    if (getUserByIdState.apiState === 'success' && getUserByIdState.data) {
      const userData = getUserByIdState.data;
      // Split name into first/last if first_name/last_name not available
      let firstName = userData.first_name || '';
      let lastName = userData.last_name || '';
      if (!firstName && !lastName && userData.name) {
        const parts = userData.name.trim().split(/\s+/);
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
      form.setFieldsValue({
        first_name: firstName,
        last_name: lastName,
        email: userData.email || '',
      });
      getUserByIdReset();
    } else if (getUserByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getUserByIdState.message || 'Failed to fetch profile data',
      });
      getUserByIdReset();
    }
  }, [getUserByIdState, form, getUserByIdReset]);

  // Handle update response - only react if modal is open (our own update, not UserManagement's)
  useEffect(() => {
    if (updateUserState.apiState === 'success' && open) {
      const updatedUser = updateUserState.data;

      // Only update localStorage if it's the logged-in user
      if (updatedUser?._id === userId || updatedUser?.id === userId) {
        const currentUserData = getUserData();
        const updatedUserData = {
          ...currentUserData,
          name: updatedUser.name,
        };
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUserData));
      }

      notification.success({
        message: 'Success',
        description: updateUserState.message || 'Profile updated successfully',
      });

      setLoading(false);
      updateUserReset();
      onClose();
      // Force reload to update sidebar name
      window.location.reload();
    } else if (updateUserState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateUserState.message || 'Failed to update profile',
      });
      setLoading(false);
      updateUserReset();
    }
  }, [updateUserState, updateUserReset, onClose, open, userId]);

  // Handle form submit
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const updateData = {
        first_name: values.first_name,
        last_name: values.last_name,
        name: [values.first_name, values.last_name].filter(Boolean).join(' ').trim(),
      };

      updateUser(userId, updateData);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'An error occurred. Please try again.',
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <ThemeModal
      title="Edit Profile"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={loading}
      width={450}
      destroyOnClose
      maskClosable={false}
    >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  message: 'Please enter first name',
                },
              ]}
            >
              <StyledInput placeholder="First name" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="last_name"
            >
              <StyledInput placeholder="Last name" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Email"
              name="email"
            >
              <StyledInput
                placeholder="Email address"
                readOnly
                disabled
                style={{
                  backgroundColor: '#f5f5f5',
                  cursor: 'not-allowed',
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </StyledForm>
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  getUserByIdState: state.user.getUserByIdState,
  updateUserState: state.user.updateUserState,
});

const mapDispatchToProps = (dispatch) => ({
  getUserById: (id) => dispatch(getUserByIdApi(id)),
  getUserByIdReset: () => dispatch(getUserByIdReset()),
  updateUser: (id, data) => dispatch(updateUserApi(id, data)),
  updateUserReset: () => dispatch(updateUserReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEditModal);
